import { browserSupportsWebAuthn, startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { User } from "../data/User";
import { ApiResponse, ApiStatus, callApi } from "./ApiUtilities";

export interface PasskeyInfo {
  id: string;
  name: string;
  /** synced (iCloud Keychain, Google Password Manager...) rather than on one device */
  isBackedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

interface PasskeyCeremony {
  challengeId: string;
  /** Fido2NetLib options JSON, in the shape @simplewebauthn/browser expects */
  optionsJson: string;
}

export function passkeysSupported(): boolean {
  return browserSupportsWebAuthn();
}

function dataOrThrow<T>(response: ApiResponse<T>): T {
  if (response.status === ApiStatus.Ok) {
    return response.data as T;
  } else if (response.status === ApiStatus.Unauthorised) {
    throw Error(`Not logged in, try refreshing page (${response.message}`);
  }
  throw Error(response.message || "Something went wrong. Please try again.");
}

/** Turns browser/WebAuthn errors into something a person can act on. */
export function friendlyPasskeyError(error: unknown, registering: boolean): Error {
  const name = (error as { name?: string })?.name ?? "";
  const code = (error as { code?: string })?.code ?? "";
  if (name === "InvalidStateError" || code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED") {
    return Error("This device already has a passkey for your account.");
  }
  if (name === "NotAllowedError" || code === "ERROR_CEREMONY_ABORTED") {
    return Error(registering ? "Passkey setup was cancelled or timed out." : "Passkey sign-in was cancelled or timed out.");
  }
  if (error instanceof Error && error.message) {
    return error;
  }
  return Error("Something went wrong with the passkey. Please try again.");
}

export async function getPasskeys(): Promise<PasskeyInfo[]> {
  return dataOrThrow(await callApi<PasskeyInfo[]>("/api/passkeys", "GET"));
}

export async function deletePasskey(id: string): Promise<void> {
  dataOrThrow(await callApi<string>(`/api/passkeys/${encodeURIComponent(id)}`, "DELETE"));
}

/** Creates a passkey on this device (or phone/security key) for the signed-in user. */
export async function addPasskey(name: string): Promise<PasskeyInfo> {
  const ceremony = dataOrThrow(await callApi<PasskeyCeremony>("/api/passkeys/register/options", "POST"));
  let response;
  try {
    response = await startRegistration({ optionsJSON: JSON.parse(ceremony.optionsJson) });
  } catch (error) {
    throw friendlyPasskeyError(error, true);
  }
  return dataOrThrow(await callApi<PasskeyInfo>("/api/passkeys/register/verify", "POST", { challengeId: ceremony.challengeId, response, name }));
}

/** Signs in with any passkey this browser has for the site; returns the signed-in user. */
export async function signInWithPasskey(): Promise<User> {
  const ceremony = dataOrThrow(await callApi<PasskeyCeremony>("/api/auth/passkey/options", "POST"));
  let response;
  try {
    response = await startAuthentication({ optionsJSON: JSON.parse(ceremony.optionsJson) });
  } catch (error) {
    throw friendlyPasskeyError(error, false);
  }
  const result = await callApi<User>("/api/auth/passkey/verify", "POST", { challengeId: ceremony.challengeId, response });
  if (result.status === ApiStatus.Ok && result.data) {
    return result.data;
  }
  throw Error(result.message || "Passkey sign-in failed. Please try again.");
}

/** A sensible default name for a new passkey, from the browser's platform, e.g. "iPhone", "Mac". */
export function defaultPasskeyName(userAgent: string = navigator.userAgent): string {
  if (/iPhone/i.test(userAgent)) return "iPhone";
  if (/iPad/i.test(userAgent)) return "iPad";
  if (/Android/i.test(userAgent)) return "Android device";
  if (/Macintosh|Mac OS X/i.test(userAgent)) return "Mac";
  if (/Windows/i.test(userAgent)) return "Windows PC";
  if (/CrOS/i.test(userAgent)) return "Chromebook";
  if (/Linux/i.test(userAgent)) return "Linux computer";
  return "Passkey";
}
